import React, {useCallback, useState} from 'react'
import ToolbarCustom from '../../../_metronic/layout/components/toolbar/toolbars/ToolbarCustom'
import config from '../../../config.json'
import {fetchEmailSendings} from '../../services/firebase'
import useEmailPages from './useEmailPages'
import EmailSendingDetailModal from './EmailSendingDetailModal'
import {DeliveryCounts, displayValue, formatDate, Loading, Pagination, RequestError, Status, statusLabel} from './EmailSendingComponents'

const EmailSendings = () => {
  const production = config.environment === 'production'
  const [status, setStatus] = useState('')
  const [environment, setEnvironment] = useState('')
  const fetchPage = useCallback(cursor => fetchEmailSendings(cursor, {status, environment}), [status, environment])
  const pager = useEmailPages(fetchPage, production)
  const [sendingId, setSendingId] = useState(null)

  return (
    <>
      <ToolbarCustom pageTitle='Email sendings' loading={pager.loading} filterElements={production ? [
        <button type='button' className='btn btn-sm btn-primary' disabled={pager.loading} onClick={pager.refresh}>Refresh</button>,
      ] : []} />
      {!production ? (
        <div role='status' className='alert alert-info mt-5'>
          Email tracking is available only in production admin. It includes sendings from all environments.
        </div>
      ) : (
        <div className='card mt-5'>
          <div className='card-body'>
            <div className='row g-4 align-items-end mb-5'>
              <div className='col-sm-6 col-lg-4'>
                <label className='form-label' htmlFor='email-sending-status'>Status</label>
                <select id='email-sending-status' className='form-select form-select-sm' value={status} onChange={event => setStatus(event.target.value)}>
                  <option value=''>All statuses</option>
                  <option value='any_failure'>Any failure</option>
                  <option value='failed'>Submission failed</option>
                  <option value='permanent_failure'>Permanent delivery failure</option>
                  <option value='temporary_failure'>Temporary delivery failure</option>
                  <option value='unknown'>Submission unknown</option>
                  <option value='pending'>Submission pending</option>
                  <option value='accepted'>Submission accepted</option>
                  <option value='delivered'>At least one recipient delivered</option>
                </select>
              </div>
              <div className='col-sm-6 col-lg-4'>
                <label className='form-label' htmlFor='email-sending-environment'>Originating environment</label>
                <select id='email-sending-environment' className='form-select form-select-sm' value={environment} onChange={event => setEnvironment(event.target.value)}>
                  <option value=''>All environments</option>
                  <option value='production'>Production</option>
                  <option value='staging'>Staging</option>
                  <option value='development'>Development</option>
                </select>
              </div>
              <div className='col-lg-auto'>
                <button type='button' className='btn btn-sm btn-light' disabled={!status && !environment} onClick={() => {setStatus(''); setEnvironment('')}}>Clear filters</button>
              </div>
            </div>
            {['any_failure', 'permanent_failure', 'temporary_failure'].includes(status) && (
              <p className='text-muted'>Failure filters use current recipient delivery states{status === 'any_failure' ? ' or a failed submission' : ''}. Resolved delivery failures remain visible in event history.</p>
            )}
            <p className='text-muted'>Most recently updated first · 25 per page</p>
            <p className='text-muted'>Submission accepted means Mailgun accepted the email; recipient delivery is tracked separately. Refresh to see the latest updates.</p>
            <RequestError error={pager.error} retry={pager.retry} />
            {pager.loading && <Loading />}
            <div className='table-responsive' aria-busy={pager.loading}>
              <table className='table table-row-dashed align-middle gs-0 gy-4'>
                <thead><tr className='fw-bold text-muted'>
                  {['Last updated', 'Subject / To', 'Email type', 'Channel', 'Environment', 'Submission', 'Delivery counts', 'Actions'].map(label => <th key={label} scope='col'>{label}</th>)}
                </tr></thead>
                <tbody>
                  {pager.page?.records.map(sending => (
                    <tr key={sending.id}>
                      <td className='text-nowrap'>{formatDate(sending.meta?.updated)}</td>
                      <td style={{minWidth: 220, maxWidth: 360}} className='text-break'>
                        <div className='fw-bold'>{displayValue(sending.subject)}</div>
                        <div className='text-muted'>{sending.to?.length ? sending.to.join(', ') : '—'}</div>
                      </td>
                      <td>{statusLabel(sending.email_type)}</td>
                      <td>{displayValue(sending.channel)}</td>
                      <td>{displayValue(sending.environment)}</td>
                      <td><Status value={sending.submission_status} /></td>
                      <td style={{minWidth: 220}}><DeliveryCounts counts={sending.delivery_counts} /></td>
                      <td><button type='button' className='btn btn-sm btn-light-primary text-nowrap' onClick={() => setSendingId(sending.id)}>View details</button></td>
                    </tr>
                  ))}
                  {!pager.loading && !pager.error && pager.page?.records.length === 0 && (
                    <tr><td colSpan={8} className='text-center py-8'>{status || environment ? 'No email sendings match these filters.' : 'No email sendings found.'}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination pager={pager} label='Email sendings' />
          </div>
        </div>
      )}
      {production && sendingId && <EmailSendingDetailModal key={sendingId} sendingId={sendingId} onClose={() => setSendingId(null)} />}
    </>
  )
}

export default EmailSendings
